import React, { useContext, useEffect, useCallback } from "react";
import { DashboardContext } from "context/DashboardContext";
import { ModalContext } from "context/ModalContext";
import { api_srv } from "services";
import { show_toast } from "utils/helpers";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";

export default function Add_Rate({ fetchCharges }) {
  const { charge, setCharge } = useContext(DashboardContext);
  const { modalClose } = useContext(ModalContext);

  // Memoize initialize to prevent re-creation on every render
  const initialize = useCallback(async () => {
    try {
      const charge_resp = await (await api_srv).list_rates();
      setCharge((prev) => ({ ...prev, charges: charge_resp.charges }));
    } catch (err) {
      console.error("Error fetching charges:", err);
    }
  }, [setCharge]);

  useEffect(() => {
    initialize();
    setCharge((prev) => ({
      ...prev,
      rate_type: prev?.rate_type || "fixed",
      slab_value_type: prev?.slab_value_type || "fixed",
      slabs: prev?.rate_type === "slab" && prev?.slabs?.length === 0 ? [{ min: "", max: "", value: "" }] : prev?.slabs || [],
      rateTable: prev?.rate_type === "band" && prev?.rateTable?.length === 0 ? [{ bandNumber: 1, sumAssured: { min: "", max: "" }, termRate: { "5": "" } }] : prev?.rateTable || [],
    }));
  }, [initialize, setCharge]);

  const addCharge = async (values) => {
    console.log("Submitting charge:", values);
    setCharge((prev) => ({ ...prev, loading: true }));
    try {
      // Map values to match JSON structure
      const payload = {
        rateCode: values.code,
        version: "v1", // Hardcoded as per JSON
        rateName: values.name,
        rateDescription: values.description,
        rateType: values.rate_type.toUpperCase(),
        rateTable: values.rate_type === "band" ? values.rateTable : [],
        rateSlab: values.rate_type === "slab" ? values.slabs : [],
        rateValue: ["fixed", "percentage"].includes(values.rate_type) ? parseFloat(values.value) || 0 : 0,
      };
      await (await api_srv).create_rate(payload);
      setCharge((prev) => ({ ...prev, loading: false }));
       fetchCharges();
      show_toast(`Charge ${values.name} has been added`, "success");
      modalClose();
    } catch (err) {
      console.error("Error adding charge:", err);
      setCharge((prev) => ({ ...prev, loading: false }));
      show_toast(`Failed to add charge: ${err.message}`, "error");
    }
  };

  const handleChargeChange = (evt) => {
    const { value, name } = evt.target;
    setCharge((prev) => ({ ...prev, [name]: value }));
  };

  const handleSlabChange = (index, field, value) => {
    setCharge((prev) => {
      const newSlabs = [...prev.slabs];
      newSlabs[index] = { ...newSlabs[index], [field]: value };
      return { ...prev, slabs: newSlabs };
    });
  };

  const addSlab = () => {
    setCharge((prev) => ({
      ...prev,
      slabs: [...prev.slabs, { min: "", max: "", value: "" }],
    }));
  };

  const removeSlab = (index) => {
    setCharge((prev) => ({
      ...prev,
      slabs: prev.slabs.filter((_, i) => i !== index),
    }));
  };

  const handleBandChange = (index, field, subField, value) => {
    setCharge((prev) => {
      const newRateTable = [...prev.rateTable];
      if (field === "sumAssured") {
        newRateTable[index] = {
          ...newRateTable[index],
          sumAssured: { ...newRateTable[index].sumAssured, [subField]: value },
        };
      } else if (field === "termRate") {
        newRateTable[index] = {
          ...newRateTable[index],
          termRate: { ...newRateTable[index].termRate, [subField]: value },
        };
      } else {
        newRateTable[index] = { ...newRateTable[index], [field]: value };
      }
      return { ...prev, rateTable: newRateTable };
    });
  };

  const addBand = () => {
    setCharge((prev) => ({
      ...prev,
      rateTable: [
        ...prev.rateTable,
        { bandNumber: prev.rateTable.length + 1, sumAssured: { min: "", max: "" }, termRate: { "5": "" } },
      ],
    }));
  };

  const removeBand = (index) => {
    setCharge((prev) => ({
      ...prev,
      rateTable: prev.rateTable
        .filter((_, i) => i !== index)
        .map((band, i) => ({ ...band, bandNumber: i + 1 })), // Reassign bandNumber
    }));
  };

  const addTermRate = (index) => {
    setCharge((prev) => {
      const newRateTable = [...prev.rateTable];
      const currentTerms = Object.keys(newRateTable[index].termRate);
      const newTerm = (parseInt(currentTerms[currentTerms.length - 1]) + 1 || 5).toString();
      newRateTable[index].termRate[newTerm] = "";
      return { ...prev, rateTable: newRateTable };
    });
  };

  const removeTermRate = (index, term) => {
    setCharge((prev) => {
      const newRateTable = [...prev.rateTable];
      const { [term]: _, ...rest } = newRateTable[index].termRate;
      newRateTable[index].termRate = rest;
      return { ...prev, rateTable: newRateTable };
    });
  };

  return (
    <div className="p-4 bg-white rounded shadow">
      <Formik
        enableReinitialize
        initialValues={charge}
        validationSchema={Yup.object().shape({
          name: Yup.string().required("Name is required"),
          code: Yup.string().required("Code is required"),
          description: Yup.string().required("Description is required"),
          rate_type: Yup.string().required("Please select a rate type"),
          value: Yup.string().when("rate_type", {
            is: (val) => ["fixed", "percentage"].includes(val),
            then: Yup.string().required("Value is required"),
            otherwise: Yup.string().notRequired(),
          }),
          slab_value_type: Yup.string().when("rate_type", {
            is: "slab",
            then: Yup.string().required("Slab value type is required").oneOf(["fixed", "percentage"]),
            otherwise: Yup.string().notRequired(),
          }),
          slabs: Yup.array().when("rate_type", {
            is: "slab",
            then: Yup.array()
              .of(
                Yup.object().shape({
                  min: Yup.number().required("Min is required").positive("Min must be positive"),
                  max: Yup.number().required("Max is required").positive("Max must be positive"),
                  value: Yup.number()
                    .required("Value is required")
                    .positive("Value must be positive")
                    .when("slab_value_type", {
                      is: "percentage",
                      then: Yup.number().max(100, "Percentage value must be between 0 and 100"),
                    }),
                })
              )
              .min(1, "At least one slab is required"),
            otherwise: Yup.array().notRequired(),
          }),
          rateTable: Yup.array().when("rate_type", {
            is: "band",
            then: Yup.array()
              .of(
                Yup.object().shape({
                  bandNumber: Yup.number().required("Band number is required").positive("Band number must be positive"),
                  sumAssured: Yup.object().shape({
                    min: Yup.number().required("Min is required").positive("Min must be positive"),
                    max: Yup.number().required("Max is required").positive("Max must be positive"),
                  }),
                  termRate: Yup.object().test(
                    "non-empty-term-rate",
                    "At least one term rate is required",
                    (value) => Object.keys(value).length > 0
                  ).shape({}),
                })
              )
              .min(1, "At least one band is required"),
            otherwise: Yup.array().notRequired(),
          }),
        })}
        onSubmit={addCharge}
      >
        {({ errors, touched }) => (
          <Form className="mb-0">
            {console.log("Errors:", errors)}
            <div className="row g-3">
              <div className="col-md-6">
                <label htmlFor="name" className="form-label">
                  Rate Name
                </label>
                <input
                  name="name"
                  type="text"
                  className={`form-control ${errors.name && touched.name ? "is-invalid" : ""}`}
                  onChange={handleChargeChange}
                  value={charge?.name || ""}
                />
                <ErrorMessage name="name" component="div" className="invalid-feedback" />
              </div>

              <div className="col-md-6">
                <label htmlFor="code" className="form-label">
                  Rate Code
                </label>
                <input
                  name="code"
                  type="text"
                  className={`form-control ${errors.code && touched.code ? "is-invalid" : ""}`}
                  onChange={handleChargeChange}
                  value={charge?.code || ""}
                />
                <ErrorMessage name="code" component="div" className="invalid-feedback" />
              </div>

              <div className="col-md-6">
                <label htmlFor="description" className="form-label">
                  Description
                </label>
                <textarea
                  name="description"
                  className={`form-control ${errors.description && touched.description ? "is-invalid" : ""}`}
                  onChange={handleChargeChange}
                  value={charge?.description || ""}
                  rows="4"
                />
                <ErrorMessage name="description" component="div" className="invalid-feedback" />
              </div>

              <div className="col-md-6 my-4">
                <label className="form-label">Rate Type</label>
                <div>
                  {["Fixed", "Percentage", "Slab", "Band"].map((rateType) => (
                    <div key={rateType} className="form-check">
                      <input
                        type="radio"
                        name="rate_type"
                        value={rateType.toLowerCase()}
                        checked={charge?.rate_type === rateType.toLowerCase()}
                        onChange={handleChargeChange}
                        className="form-check-input"
                      />
                      <label className="form-check-label">
                        {rateType === "Percentage" ? "Percentage (%)" : rateType}
                      </label>
                    </div>
                  ))}
                </div>
                <ErrorMessage name="rate_type" component="div" className="invalid-feedback d-block" />
              </div>

              {["fixed", "percentage"].includes(charge?.rate_type) && (
                <div className="col-md-6 my-4">
                  <label htmlFor="value" className="form-label">
                    Value
                  </label>
                  <input
                    name="value"
                    type="number"
                    className={`form-control ${errors.value && touched.value ? "is-invalid" : ""}`}
                    value={charge?.value || ""}
                    onChange={handleChargeChange}
                  />
                  <ErrorMessage name="value" component="div" className="invalid-feedback" />
                </div>
              )}

              {charge?.rate_type === "slab" && (
                <div className="col-12">
                  <label className="form-label">Slab Value Type</label>
                  <div className="mb-3">
                    {["Fixed", "Percentage"].map((type) => (
                      <div key={type} className="form-check form-check-inline">
                        <input
                          type="radio"
                          name="slab_value_type"
                          value={type.toLowerCase()}
                          checked={charge?.slab_value_type === type.toLowerCase()}
                          onChange={handleChargeChange}
                          className="form-check-input"
                        />
                        <label className="form-check-label">
                          {type === "Percentage" ? "Percentage (%)" : type}
                        </label>
                      </div>
                    ))}
                    <ErrorMessage name="slab_value_type" component="div" className="invalid-feedback d-block" />
                  </div>

                  <label className="form-label">Slab Details</label>
                  {charge?.slabs?.map((slab, index) => (
                    <div key={index} className="row g-2 mb-2 align-items-end">
                      <div className="col">
                        <label htmlFor={`slabs[${index}].min`} className="form-label">
                          Minimum
                        </label>
                        <input
                          type="number"
                          value={slab.min}
                          onChange={(e) => handleSlabChange(index, "min", e.target.value)}
                          className={`form-control ${
                            errors.slabs?.[index]?.min && touched.slabs?.[index]?.min ? "is-invalid" : ""
                          }`}
                        />
                        <ErrorMessage
                          name={`slabs[${index}].min`}
                          component="div"
                          className="invalid-feedback"
                        />
                      </div>
                      <div className="col">
                        <label htmlFor={`slabs[${index}].max`} className="form-label">
                          Maximum
                        </label>
                        <input
                          type="number"
                          value={slab.max}
                          onChange={(e) => handleSlabChange(index, "max", e.target.value)}
                          className={`form-control ${
                            errors.slabs?.[index]?.max && touched.slabs?.[index]?.max ? "is-invalid" : ""
                          }`}
                        />
                        <ErrorMessage
                          name={`slabs[${index}].max`}
                          component="div"
                          className="invalid-feedback"
                        />
                      </div>
                      <div className="col">
                        <label htmlFor={`slabs[${index}].value`} className="form-label">
                          Value {charge?.slab_value_type === "percentage" ? "(%)" : ""}
                        </label>
                        <input
                          type="number"
                          value={slab.value}
                          onChange={(e) => handleSlabChange(index, "value", e.target.value)}
                          className={`form-control ${
                            errors.slabs?.[index]?.value && touched.slabs?.[index]?.value ? "is-invalid" : ""
                          }`}
                        />
                        <ErrorMessage
                          name={`slabs[${index}].value`}
                          component="div"
                          className="invalid-feedback"
                        />
                      </div>
                      {index > 0 && (
                        <div className="col-auto">
                          <button type="button" className="btn btn-danger" onClick={() => removeSlab(index)}>
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                  <button type="button" onClick={addSlab} className="btn btn-secondary mt-2">
                    Add Slab
                  </button>
                  <ErrorMessage name="slabs" component="div" className="invalid-feedback d-block" />
                </div>
              )}

              {charge?.rate_type === "band" && (
                <div className="col-12">
                  <label className="form-label">Band Details</label>
                  {charge?.rateTable?.map((band, index) => (
                    <div key={index} className="card mb-3 p-3">
                      <h6>Band {band.bandNumber}</h6>
                      <div className="row g-2 mb-2">
                        <div className="col-md-6">
                          <label htmlFor={`rateTable[${index}].sumAssured.min`} className="form-label">
                            Sum Assured Min
                          </label>
                          <input
                            type="number"
                            value={band.sumAssured.min}
                            onChange={(e) => handleBandChange(index, "sumAssured", "min", e.target.value)}
                            className={`form-control ${
                              errors.rateTable?.[index]?.sumAssured?.min && touched.rateTable?.[index]?.sumAssured?.min ? "is-invalid" : ""
                            }`}
                          />
                          <ErrorMessage
                            name={`rateTable[${index}].sumAssured.min`}
                            component="div"
                            className="invalid-feedback"
                          />
                        </div>
                        <div className="col-md-6">
                          <label htmlFor={`rateTable[${index}].sumAssured.max`} className="form-label">
                            Sum Assured Max
                          </label>
                          <input
                            type="number"
                            value={band.sumAssured.max}
                            onChange={(e) => handleBandChange(index, "sumAssured", "max", e.target.value)}
                            className={`form-control ${
                              errors.rateTable?.[index]?.sumAssured?.max && touched.rateTable?.[index]?.sumAssured?.max ? "is-invalid" : ""
                            }`}
                          />
                          <ErrorMessage
                            name={`rateTable[${index}].sumAssured.max`}
                            component="div"
                            className="invalid-feedback"
                          />
                        </div>
                      </div>
                      <label className="form-label">Term Rates</label>
                      {Object.entries(band.termRate).map(([term, rate], termIndex) => (
                        <div key={term} className="row g-2 mb-2 align-items-end">
                          <div className="col">
                            <label className="form-label">Term (Years)</label>
                            <input
                              type="number"
                              value={term}
                              onChange={(e) => {
                                const newTerm = e.target.value;
                                setCharge((prev) => {
                                  const newRateTable = [...prev.rateTable];
                                  const { [term]: _, ...rest } = newRateTable[index].termRate;
                                  newRateTable[index].termRate = { ...rest, [newTerm]: rate };
                                  return { ...prev, rateTable: newRateTable };
                                });
                              }}
                              className="form-control"
                            />
                          </div>
                          <div className="col">
                            <label className="form-label">Rate</label>
                            <input
                              type="number"
                              value={rate}
                              onChange={(e) => handleBandChange(index, "termRate", term, e.target.value)}
                              className={`form-control ${
                                errors.rateTable?.[index]?.termRate?.[term] && touched.rateTable?.[index]?.termRate?.[term] ? "is-invalid" : ""
                              }`}
                            />
                            <ErrorMessage
                              name={`rateTable[${index}].termRate[${term}]`}
                              component="div"
                              className="invalid-feedback"
                            />
                          </div>
                          {Object.keys(band.termRate).length > 1 && (
                            <div className="col-auto">
                              <button
                                type="button"
                                className="btn btn-danger"
                                onClick={() => removeTermRate(index, term)}
                              >
                                Remove Term
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => addTermRate(index)}
                        className="btn btn-secondary mt-2"
                      >
                        Add Term Rate
                      </button>
                      {index > 0 && (
                        <button
                          type="button"
                          className="btn btn-danger mt-2 ms-2"
                          onClick={() => removeBand(index)}
                        >
                          Remove Band
                        </button>
                      )}
                    </div>
                  ))}
                  <button type="button" onClick={addBand} className="btn btn-secondary mt-2">
                    Add Band
                  </button>
                  <ErrorMessage name="rateTable" component="div" className="invalid-feedback d-block" />
                </div>
              )}
            </div>

            <div className="d-flex justify-content-start mt-6">
              <button
                type="submit"
                disabled={charge?.loading}
                className="btn btn-success d-flex align-items-start"
              >
                {charge?.loading ? "Please wait..." : "Save"}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}